/**
 * Bundle 62 - Admin analytics: Funnel + Purchase CAPI Backfill +
 * Data Health Triage. Rendered inside `AdminAnalyticsPage.jsx` as
 * three super-admin-only tabs.
 *
 * Endpoints (all require super_admin JWT):
 *   GET  /api/admin/analytics/funnel?days=N
 *   GET  /api/admin/analytics/purchase-backfill/preview?days=N
 *   POST /api/admin/analytics/purchase-backfill/execute
 *   GET  /api/admin/health/cards
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, CheckCircle2, RefreshCw, Play } from 'lucide-react';
import api from '@/utils/api';

const asFixed = (n, d = 2) => (typeof n === 'number' ? n.toFixed(d) : '-');
const asPct = (n) => (typeof n === 'number' ? `${n.toFixed(2)}%` : '-');

const StatChip = ({ label, value, testid }) => (
  <div
    className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm"
    data-testid={testid}
  >
    <div className="text-neutral-500 text-[11px] uppercase tracking-wide">{label}</div>
    <div className="font-semibold text-primary-900 text-lg">{value ?? '-'}</div>
  </div>
);

const SeverityBadge = ({ severity }) => {
  const map = {
    red: 'bg-accent-100 text-accent-800 border-accent-300',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    green: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${map[severity] || 'bg-neutral-100 text-neutral-700 border-neutral-300'}`}
      data-testid={`health-card-severity-${severity}`}
    >
      {severity}
    </span>
  );
};

// ---------------------------------------------------------------------------
// 1. Funnel diagnostic tab
// ---------------------------------------------------------------------------
export const FunnelPanel = () => {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [breakdownTab, setBreakdownTab] = useState('slug');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get(`/admin/analytics/funnel?days=${days}`);
      setData(d);
    } catch (e) {
      setErr(e?.response?.status === 403
        ? 'Requires super_admin role.'
        : (e?.response?.data?.detail || e?.message || 'Failed to load funnel'));
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const topCount = data?.overall?.[0]?.count || 0;
  const stagesWithTopDrop = useMemo(() => (data?.overall || []).map((s) => ({
    ...s,
    drop_off_pct_vs_top: (topCount && s.count != null)
      ? Number((100 * (topCount - s.count) / topCount).toFixed(2))
      : null,
  })), [data, topCount]);

  return (
    <div className="space-y-6" data-testid="funnel-panel">
      <div className="flex items-center gap-3">
        <label className="text-sm text-neutral-600">Window</label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-28" data-testid="funnel-days-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="14">14 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm" variant="outline" onClick={load}
          disabled={loading}
          data-testid="funnel-refresh-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </div>

      {err && (
        <div className="rounded-md border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800" data-testid="funnel-error">
          {err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <StatChip
              label="Median ViewContent to Purchase"
              value={data.latencies_ms?.median_view_content_to_purchase
                ? `${Math.round(data.latencies_ms.median_view_content_to_purchase / 1000)}s`
                : 'n/a'}
              testid="funnel-latency-vc-p"
            />
            <StatChip
              label="Median AddToCart to Purchase"
              value={data.latencies_ms?.median_add_to_cart_to_purchase
                ? `${Math.round(data.latencies_ms.median_add_to_cart_to_purchase / 1000)}s`
                : 'n/a'}
              testid="funnel-latency-atc-p"
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Overall funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="funnel-overall-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Events</TableHead>
                    <TableHead className="text-right">Unique users</TableHead>
                    <TableHead className="text-right">Drop from prev</TableHead>
                    <TableHead className="text-right">Drop from top</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stagesWithTopDrop.map((s) => (
                    <TableRow key={s.stage} data-testid={`funnel-row-${s.stage}`}>
                      <TableCell className="font-medium">{s.stage}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.count}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.unique_users}</TableCell>
                      <TableCell className="text-right tabular-nums text-neutral-600">{asPct(s.drop_off_pct_vs_prev)}</TableCell>
                      <TableCell className="text-right tabular-nums text-neutral-600">{asPct(s.drop_off_pct_vs_top)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                {[
                  { k: 'slug', label: 'By event slug' },
                  { k: 'method', label: 'By payment method' },
                  { k: 'authed', label: 'Authed vs anon' },
                ].map((b) => (
                  <Button
                    key={b.k} size="sm"
                    variant={breakdownTab === b.k ? 'default' : 'outline'}
                    onClick={() => setBreakdownTab(b.k)}
                    data-testid={`funnel-breakdown-${b.k}`}
                  >{b.label}</Button>
                ))}
              </div>

              {breakdownTab === 'slug' && (
                <Table data-testid="funnel-slug-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slug</TableHead>
                      <TableHead className="text-right">VC</TableHead>
                      <TableHead className="text-right">RSVP</TableHead>
                      <TableHead className="text-right">ATC</TableHead>
                      <TableHead className="text-right">IC</TableHead>
                      <TableHead className="text-right">Purchase</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_event_slug || []).map((e) => {
                      const s = Object.fromEntries((e.stages || []).map((x) => [x.stage, x.count]));
                      return (
                        <TableRow key={e.event_slug}>
                          <TableCell className="font-mono text-xs">{e.event_slug}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.view_content}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.rsvp_start}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.add_to_cart}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.initiate_checkout}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{s.purchase}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!(data.by_event_slug || []).length && (
                      <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 text-sm py-6">No event-scoped events in this window.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {breakdownTab === 'method' && (
                <Table data-testid="funnel-method-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">AddToCart</TableHead>
                      <TableHead className="text-right">InitiateCheckout</TableHead>
                      <TableHead className="text-right">Purchase</TableHead>
                      <TableHead className="text-right">Drop ATC to Purchase</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_payment_method || []).map((m) => (
                      <TableRow key={m.method} data-testid={`funnel-method-${m.method}`}>
                        <TableCell className="font-medium capitalize">{m.method}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.add_to_cart}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.initiate_checkout}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.purchase}</TableCell>
                        <TableCell className="text-right tabular-nums text-neutral-600">{asPct(m.drop_off_pct_atc_to_purchase)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {breakdownTab === 'authed' && (
                <div className="grid grid-cols-2 gap-4">
                  <StatChip
                    label="Authenticated purchases"
                    value={data.authed_vs_anon_purchase?.authed_purchases}
                    testid="funnel-authed-count"
                  />
                  <StatChip
                    label="Anonymous purchases"
                    value={data.authed_vs_anon_purchase?.anonymous_purchases}
                    testid="funnel-anon-count"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!data && !err && loading && (
        <div className="text-center py-12 text-neutral-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Purchase CAPI backfill tab
// ---------------------------------------------------------------------------
const BACKFILL_PAGE_SIZE = 25;
const BACKFILL_BATCH = 50;

export const BackfillPanel = () => {
  const [days, setDays] = useState('62');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [progress, setProgress] = useState({ dispatched: 0, skipped: 0, failed: 0, remaining: 0 });
  const [completed, setCompleted] = useState(false);
  const [page, setPage] = useState(0);
  // Bundle 63 - operator can flip the candidate pool from the strict
  // revenue enum ('paid'/'verified'/'waived'/etc.) to the extended
  // "any paid-like status" pool. Also captures the reconciliation
  // audit so the operator understands WHY the counts differ.
  const [includeStatuses, setIncludeStatuses] = useState('strict');
  const [reconcile, setReconcile] = useState(null);
  const [loadingReconcile, setLoadingReconcile] = useState(false);

  const doPreview = useCallback(async () => {
    setLoadingPreview(true); setPreviewErr(null);
    setCompleted(false); setExecuteResult(null); setPage(0);
    try {
      const { data } = await api.get(
        `/admin/analytics/purchase-backfill/preview?days=${days}&include_statuses=${includeStatuses}`,
      );
      setPreview(data);
    } catch (e) {
      setPreviewErr(e?.response?.status === 403
        ? 'Requires super_admin role.'
        : (e?.response?.data?.detail || 'Preview failed'));
    } finally { setLoadingPreview(false); }
  }, [days, includeStatuses]);

  const doReconcile = useCallback(async () => {
    setLoadingReconcile(true);
    try {
      const { data } = await api.get(
        `/admin/analytics/purchase-backfill/reconcile?days=${days}`,
      );
      setReconcile(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally { setLoadingReconcile(false); }
  }, [days]);

  // Bundle 65 - track Meta ingest verification state per event_id.
  const [verifyStatus, setVerifyStatus] = useState({}); // event_id -> status
  const [verifyCounts, setVerifyCounts] = useState(null);
  const [verifyPolling, setVerifyPolling] = useState(false);

  const runVerify = useCallback(async (eventIds) => {
    if (!eventIds?.length) return;
    setVerifyPolling(true);
    const deadline = Date.now() + 5 * 60 * 1000; // 5 min timeout
    let pending = new Set(eventIds);
    let counts = { confirmed: 0, rejected: 0, pending: eventIds.length, not_dispatched: 0 };
    try {
      while (pending.size > 0 && Date.now() < deadline) {
        try {
          const { data } = await api.post(
            '/admin/analytics/purchase-backfill/verify',
            { event_ids: Array.from(pending) },
          );
          counts = data.counts || counts;
          const nextPending = new Set();
          setVerifyStatus((prev) => {
            const next = { ...prev };
            (data.results || []).forEach((r) => {
              next[r.event_id] = r.status;
              if (r.status === 'pending') nextPending.add(r.event_id);
            });
            return next;
          });
          setVerifyCounts(counts);
          pending = nextPending;
        } catch (_) { /* keep polling */ }
        if (pending.size === 0) break;
        // DELIBERATELY sleep between poll ticks (3s) to give Meta's
        // async pipeline time to acknowledge and to avoid hammering
        // our verify endpoint at request-rate speed.
        await new Promise((r) => setTimeout(r, 3000));
      }
      // Timeout: anything still pending flips to amber.
      if (pending.size > 0) {
        setVerifyStatus((prev) => {
          const next = { ...prev };
          pending.forEach((eid) => { next[eid] = 'timeout'; });
          return next;
        });
      }
    } finally { setVerifyPolling(false); }
  }, []);

  const doExecute = useCallback(async () => {
    setConfirmOpen(false);
    setExecuting(true);
    setVerifyStatus({});
    setVerifyCounts(null);
    let dispatched = 0, skipped = 0, failed = 0, remaining = preview?.total_candidates || 0;
    setProgress({ dispatched, skipped, failed, remaining });
    const allEventIds = [];
    try {
      // Loop batches until drained.
      let looping = true;
      while (looping) {
        const { data } = await api.post(
          '/admin/analytics/purchase-backfill/execute',
          {
            confirm: true, days: Number(days), batch_size: BACKFILL_BATCH,
            include_statuses: includeStatuses,
          },
        );
        dispatched += data.dispatched || 0;
        skipped += data.skipped_already_present || 0;
        failed += (data.failed || []).length;
        remaining = data.remaining_after_batch || 0;
        (data.sample || []).forEach((s) => {
          if (s.expected_event_id) allEventIds.push(s.expected_event_id);
        });
        setProgress({ dispatched, skipped, failed, remaining });
        if (remaining === 0) looping = false;
        if ((data.processed || 0) === 0) looping = false;
      }
      setExecuteResult({ dispatched, skipped, failed });
      setCompleted(true);
      toast.success(`Backfill complete: ${dispatched} dispatched, ${skipped} skipped, ${failed} failed`);
      // Bundle 65 - kick off Meta ingest verification.
      if (allEventIds.length > 0) {
        runVerify(allEventIds);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Backfill failed mid-run');
    } finally { setExecuting(false); }
  }, [days, preview, includeStatuses, runVerify]);

  const visibleCandidates = useMemo(() => {
    const all = preview?.full || [];
    return all.slice(page * BACKFILL_PAGE_SIZE, (page + 1) * BACKFILL_PAGE_SIZE);
  }, [preview, page]);
  const totalPages = Math.ceil((preview?.full?.length || 0) / BACKFILL_PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="backfill-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-neutral-600">Window</label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-28" data-testid="backfill-days-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="62">62 days (max)</SelectItem>
          </SelectContent>
        </Select>
        <label className="text-sm text-neutral-600">Statuses</label>
        <Select value={includeStatuses} onValueChange={setIncludeStatuses}>
          <SelectTrigger className="w-44" data-testid="backfill-include-statuses-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="strict">Strict revenue enum</SelectItem>
            <SelectItem value="all">All paid-like statuses</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm" onClick={doPreview}
          disabled={loadingPreview}
          data-testid="backfill-preview-btn"
        >
          {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Preview backfill
        </Button>
        <Button
          size="sm" variant="outline" onClick={doReconcile}
          disabled={loadingReconcile}
          data-testid="backfill-reconcile-btn"
        >
          {loadingReconcile ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Reconcile
        </Button>
        <Button
          size="sm" variant="default"
          disabled={!preview || executing || completed}
          onClick={() => setConfirmOpen(true)}
          data-testid="backfill-execute-btn"
        >
          {executing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
          {completed ? 'Backfill complete for this window' : 'Execute backfill'}
        </Button>
      </div>

      {previewErr && (
        <div className="rounded-md border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800" data-testid="backfill-error">
          {previewErr}
        </div>
      )}

      {reconcile && (
        <Card className="border-primary-200 bg-primary-50/40" data-testid="backfill-reconcile-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reconciliation audit</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {reconcile.backfill_candidates?.queue_empty && (
              <div
                className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2"
                data-testid="backfill-all-caught-up"
              >
                <CheckCircle2 className="h-4 w-4 mt-0.5" />
                <div>
                  <div className="font-semibold">All caught up</div>
                  <div className="text-xs">
                    Every paid transaction in the last {reconcile.days} days has
                    already been dispatched to Meta. Meta CAPI shows{' '}
                    <strong>{reconcile.revenue_recognition?.total_purchase_logs_in_window ?? 0}</strong>{' '}
                    Purchase events in this window (
                    <strong>{reconcile.revenue_recognition?.total_purchase_logs_ever ?? 0}</strong> lifetime).
                    Execute below will do nothing until new payments come in.
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatChip
                label="Confirmed regs"
                value={reconcile.confirmed_registrations?.total}
                testid="reconcile-stat-confirmed"
              />
              <StatChip
                label="Regs with txn row"
                value={reconcile.confirmed_registrations?.with_matching_txn}
                testid="reconcile-stat-with-txn"
              />
              <StatChip
                label="Orphan regs (no txn)"
                value={reconcile.confirmed_registrations?.orphaned_no_txn_row}
                testid="reconcile-stat-orphans"
              />
              <StatChip
                label={`Purchases sent to Meta (${reconcile.days}d)`}
                value={reconcile.revenue_recognition?.total_purchase_logs_in_window}
                testid="reconcile-stat-logged"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatChip
                label="Backfill queue (strict)"
                value={reconcile.backfill_candidates?.strict}
                testid="reconcile-stat-strict"
              />
              <StatChip
                label="Backfill queue (extended)"
                value={reconcile.backfill_candidates?.extended}
                testid="reconcile-stat-extended"
              />
              <StatChip
                label="Extended gain"
                value={`+${reconcile.backfill_candidates?.extended_gain_over_strict ?? 0}`}
                testid="reconcile-stat-gain"
              />
            </div>
            {reconcile.status_distribution?.length > 0 && (
              <div className="pt-2">
                <div className="text-xs text-neutral-500 mb-1">payment_transactions status distribution ({reconcile.days}d)</div>
                <div className="flex flex-wrap gap-2 text-xs font-mono">
                  {reconcile.status_distribution.map((s) => (
                    <span
                      key={s.payment_status}
                      className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-1"
                      data-testid={`reconcile-status-${s.payment_status}`}
                    >
                      <strong className="text-primary-900">{s.count}</strong>
                      <span className="text-neutral-600">{s.payment_status}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip label="Total candidates" value={preview.total_candidates} testid="backfill-stat-total" />
            <StatChip label="Missing user PII" value={preview.concerns?.missing_user_pii} testid="backfill-stat-nopii" />
            <StatChip label="Outside 62d window" value={preview.concerns?.outside_meta_62d_window} testid="backfill-stat-outside" />
            <StatChip
              label="Age range (days)"
              value={preview.age_days?.min != null
                ? `${asFixed(preview.age_days.min, 1)} - ${asFixed(preview.age_days.max, 1)}`
                : 'n/a'}
              testid="backfill-stat-age"
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">By payment method</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                {['stripe', 'zelle', 'venmo', 'paypal'].map((m) => (
                  <StatChip
                    key={m} label={m}
                    value={preview.by_method?.[m] ?? 0}
                    testid={`backfill-stat-method-${m}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {preview.by_status && Object.keys(preview.by_status).length > 0 && (
            <Card data-testid="backfill-status-distribution-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">By payment_status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 text-xs font-mono">
                  {Object.entries(preview.by_status)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-1"
                        data-testid={`backfill-status-${k}`}
                      >
                        <strong className="text-primary-900">{v}</strong>
                        <span className="text-neutral-600">{k}</span>
                      </span>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {executing && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-neutral-700 mb-2">
                  Dispatching... {progress.dispatched + progress.skipped} of {preview.total_candidates}
                </div>
                <div className="h-2 w-full bg-neutral-200 rounded overflow-hidden">
                  <div
                    className="h-2 bg-primary-700 transition-all"
                    style={{ width: `${Math.min(100, 100 * (progress.dispatched + progress.skipped) / Math.max(1, preview.total_candidates))}%` }}
                    data-testid="backfill-progress-bar"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {executeResult && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4 flex items-center gap-4 text-sm" data-testid="backfill-result">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                <span><strong>{executeResult.dispatched}</strong> dispatched</span>
                <span><strong>{executeResult.skipped}</strong> already present</span>
                <span><strong>{executeResult.failed}</strong> failed</span>
              </CardContent>
            </Card>
          )}

          {verifyCounts && (
            <Card
              className={verifyCounts.confirmed > 0 && verifyCounts.rejected === 0 && verifyCounts.pending === 0
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-primary-200 bg-primary-50/40'}
              data-testid="backfill-verify-summary"
            >
              <CardContent className="p-4 flex items-center gap-4 text-sm flex-wrap">
                {verifyPolling ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary-700" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                )}
                <span className="font-semibold">Meta ingest verification</span>
                <span className="text-emerald-800">
                  <strong>{verifyCounts.confirmed || 0}</strong> confirmed
                </span>
                <span className="text-accent-800">
                  <strong>{verifyCounts.rejected || 0}</strong> rejected
                </span>
                <span className="text-primary-800">
                  <strong>{verifyCounts.pending || 0}</strong> pending
                </span>
                <span className="text-neutral-600">
                  <strong>{verifyCounts.not_dispatched || 0}</strong> not dispatched
                </span>
                {verifyPolling && (
                  <span className="text-xs text-neutral-500">
                    Polling Meta every 3s (5 min timeout)
                  </span>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Candidate transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table className="font-mono text-xs" data-testid="backfill-candidates-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Paid at</TableHead>
                    <TableHead className="text-right">Age (d)</TableHead>
                    <TableHead>PII</TableHead>
                    <TableHead>Meta</TableHead>
                    <TableHead>Ingest</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleCandidates.map((c) => {
                    const st = verifyStatus[c.expected_event_id];
                    let ingestLabel = 'not run';
                    let ingestCls = 'text-neutral-400';
                    if (st === 'confirmed') { ingestLabel = 'confirmed'; ingestCls = 'text-success-800 font-semibold'; }
                    else if (st === 'rejected') { ingestLabel = 'rejected'; ingestCls = 'text-accent-800 font-semibold'; }
                    else if (st === 'pending') { ingestLabel = 'polling...'; ingestCls = 'text-primary-800'; }
                    else if (st === 'timeout') { ingestLabel = 'timeout'; ingestCls = 'text-yellow-800'; }
                    else if (st === 'not_dispatched') { ingestLabel = 'no dispatch'; ingestCls = 'text-neutral-500'; }
                    return (
                      <TableRow key={c.transaction_id}>
                        <TableCell className="truncate max-w-[180px]">{c.transaction_id}</TableCell>
                        <TableCell>{c.method}</TableCell>
                        <TableCell className="text-right tabular-nums">${((c.amount_cents || 0) / 100).toFixed(2)}</TableCell>
                        <TableCell className="text-neutral-600">{(c.event_time_iso || '').slice(0, 19)}</TableCell>
                        <TableCell className="text-right tabular-nums">{asFixed(c.age_days, 1)}</TableCell>
                        <TableCell>{c.has_user_pii ? 'yes' : 'no'}</TableCell>
                        <TableCell>{c.outside_meta_window ? 'expired' : 'ok'}</TableCell>
                        <TableCell
                          className={ingestCls}
                          data-testid={`backfill-ingest-${c.transaction_id}`}
                        >
                          {st === 'confirmed' && <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />}
                          {ingestLabel}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!visibleCandidates.length && (
                    <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-6 font-sans">Nothing to backfill in this window.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="mt-3 flex justify-between text-xs">
                  <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                  <span className="text-neutral-500">Page {page + 1} of {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="backfill-confirm-modal">
          <DialogHeader>
            <DialogTitle>Confirm CAPI backfill</DialogTitle>
            <DialogDescription>
              This will dispatch <strong>{preview?.total_candidates || 0}</strong>
              {' '}Purchase events to Meta CAPI with real historical timestamps.
              Idempotent on {`event_id = purchase_{txn.id}`}. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="backfill-confirm-cancel">Cancel</Button>
            <Button onClick={doExecute} data-testid="backfill-confirm-yes">Yes, dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Data Health triage tab
// ---------------------------------------------------------------------------
export const HealthTriagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get('/admin/health/cards');
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load health cards');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || [];
  const filtered = showAll ? cards : cards.filter((c) => c.severity !== 'green');
  const greenCount = cards.filter((c) => c.severity === 'green').length;

  return (
    <div className="space-y-4" data-testid="health-triage-panel">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 text-xs">
          <Badge className="bg-accent-100 text-accent-800 border-accent-300">
            {data?.meta?.counts?.red ?? 0} red
          </Badge>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300">
            {data?.meta?.counts?.amber ?? 0} amber
          </Badge>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
            {greenCount} green
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => setShowAll((v) => !v)}
            data-testid="health-triage-toggle-all"
          >
            {showAll ? 'Hide green' : `Show all ${cards.length} cards`}
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {err && (
        <div className="rounded-md border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800">
          {typeof err === 'string' ? err : JSON.stringify(err)}
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((c) => (
          <Card
            key={c.id}
            className={c.severity === 'red' ? 'border-accent-300' : c.severity === 'amber' ? 'border-amber-300' : ''}
            data-testid={`health-triage-card-${c.id}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {c.severity !== 'green' && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    {c.title}
                  </CardTitle>
                  <div className="mt-1 flex gap-2 text-xs">
                    <SeverityBadge severity={c.severity} />
                    <span className="text-neutral-500">count: <strong>{c.count ?? '-'}</strong></span>
                    {c.snoozed && <span className="text-neutral-500">(snoozed)</span>}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 text-sm space-y-3">
              <p className="text-neutral-700">{c.detail}</p>
              {c.recommended_fix && (
                <div className="rounded-md bg-neutral-50 border border-neutral-200 p-3 text-xs" data-testid={`health-triage-fix-${c.id}`}>
                  <div className="font-semibold text-neutral-700 mb-1">Fix guidance</div>
                  <div className="text-neutral-600">{c.recommended_fix}</div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {!filtered.length && !loading && (
          <div className="text-center text-neutral-500 text-sm py-8" data-testid="health-triage-empty">
            {showAll ? 'No cards.' : 'All green. Nothing to triage.'}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4. Bundle 63 - Meta Custom Audience export tab
// ---------------------------------------------------------------------------
export const AudiencesPanel = () => {
  // Default scope 'paid_only' - best Lookalike seed per Meta guidance.
  const [scope, setScope] = useState('paid_only');
  const [days, setDays] = useState('all');
  const [eventSlug, setEventSlug] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const qs = useMemo(() => {
    const parts = [`scope=${encodeURIComponent(scope)}`];
    if (days !== 'all') parts.push(`days=${encodeURIComponent(days)}`);
    if (eventSlug.trim()) parts.push(`event_slug=${encodeURIComponent(eventSlug.trim())}`);
    return parts.join('&');
  }, [scope, days, eventSlug]);

  const doPreview = useCallback(async () => {
    setLoadingPreview(true); setPreviewErr(null);
    try {
      const { data } = await api.get(
        `/admin/analytics/custom-audience/preview?${qs}`,
      );
      setPreview(data);
    } catch (e) {
      setPreviewErr(e?.response?.status === 403
        ? 'Requires super_admin role.'
        : (e?.response?.data?.detail || 'Preview failed'));
    } finally { setLoadingPreview(false); }
  }, [qs]);

  useEffect(() => { doPreview(); }, [doPreview]);

  const doDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const resp = await api.get(
        `/admin/analytics/custom-audience.csv?${qs}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `dostination_custom_audience_${scope}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      // Bundle 166 · read blob-as-text on error so the real
      // backend error reaches the toast. axios with
      // `responseType: 'blob'` returns error bodies as a Blob too,
      // which the default `e?.response?.data?.detail` path cannot
      // read - surfacing a generic "Download failed" masks the
      // real cause (401 expired session, 403 role check, 500).
      let msg = 'Download failed';
      try {
        const errBlob = e?.response?.data;
        if (errBlob && typeof errBlob.text === 'function') {
          const text = await errBlob.text();
          try {
            const parsed = JSON.parse(text);
            msg = parsed?.detail?.message || parsed?.detail || parsed?.message || msg;
          } catch (_parseErr) {
            if (text) msg = text.slice(0, 200);
          }
        } else if (typeof errBlob === 'string' && errBlob) {
          msg = errBlob.slice(0, 200);
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        }
        if (e?.response?.status) {
          msg = `${msg} (HTTP ${e.response.status})`;
        }
      } catch (_readErr) {
        // fall through with the default msg
      }
      toast.error(msg);
    } finally { setDownloading(false); }
  }, [qs, scope]);

  return (
    <div className="space-y-4" data-testid="audiences-panel">
      <div className="rounded-md border border-neutral-200 bg-white p-4 text-sm text-neutral-700">
        <div className="font-semibold text-primary-900 mb-1">Meta Custom Audience export</div>
        <div className="text-neutral-600">
          Every PII column is SHA-256 hashed on our side per Meta&apos;s
          Custom Audience CSV spec. <code>extern_id</code> is the only
          plaintext column (Meta accepts plaintext external_id).
          Paid-only is the recommended Lookalike seed - Meta&apos;s
          match-quality model weights it heavier than pure volume.
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-neutral-600">Scope</label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56" data-testid="audiences-scope-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paid_only">Paid only (recommended seed)</SelectItem>
            <SelectItem value="all_registrations">All registrations</SelectItem>
          </SelectContent>
        </Select>
        <label className="text-sm text-neutral-600">Window</label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32" data-testid="audiences-days-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="365">365 days</SelectItem>
          </SelectContent>
        </Select>
        <input
          type="text"
          value={eventSlug}
          onChange={(e) => setEventSlug(e.target.value)}
          placeholder="event slug (optional)"
          className="border border-neutral-200 rounded-md px-3 py-1.5 text-sm w-64"
          data-testid="audiences-event-slug-input"
        />
        <Button
          size="sm" variant="outline" onClick={doPreview}
          disabled={loadingPreview}
          data-testid="audiences-preview-btn"
        >
          {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Preview
        </Button>
        <Button
          size="sm" onClick={doDownload}
          disabled={downloading || !preview || preview?.stats?.row_count === 0}
          data-testid="audiences-download-btn"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
          Download Custom Audience CSV
        </Button>
      </div>

      {previewErr && (
        <div className="rounded-md border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800" data-testid="audiences-error">
          {previewErr}
        </div>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip
              label="Rows"
              value={preview.stats?.row_count}
              testid="audiences-stat-rows"
            />
            <StatChip
              label="With email hash"
              value={preview.stats?.with_email_hash}
              testid="audiences-stat-email"
            />
            <StatChip
              label="With phone hash"
              value={preview.stats?.with_phone_hash}
              testid="audiences-stat-phone"
            />
            <StatChip
              label="With extern_id"
              value={preview.stats?.with_extern_id}
              testid="audiences-stat-externid"
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Sample rows (first 5, hashed)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="font-mono text-[10px]" data-testid="audiences-sample-table">
                  <TableHeader>
                    <TableRow>
                      {(preview.columns || []).map((c) => (
                        <TableHead key={c} className="whitespace-nowrap">{c}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(preview.sample || []).map((row, i) => (
                      <TableRow key={i}>
                        {preview.columns.map((c) => (
                          <TableCell key={c} className="truncate max-w-[120px]">
                            {row[c] ? String(row[c]).slice(0, 12) + (String(row[c]).length > 12 ? '...' : '') : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {(!preview.sample || preview.sample.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={(preview.columns || []).length} className="text-center text-neutral-500 py-6 font-sans">
                          No rows match this scope.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const AdminAnalyticsBundle62Sections = { FunnelPanel, BackfillPanel, HealthTriagePanel, AudiencesPanel };
export default AdminAnalyticsBundle62Sections;
